import { TabBar, useDefaultSelecting } from '@bp/ui-components';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useMemorizedCacheTag, useMemorizedCacheTags } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  useCreateLessonsFromCurriculaMutation,
  useCurriculaLessonStatusQuery,
  useCurriculaListQuery,
} from '../../../types/planung-graphql-client-defs';
import { selectedToData } from '../../../utils/selectedToData';
import { showSuccessToast, showUserErrorToast } from '../../../utils/toast';
import { ModalBottomButtons } from '../../ModalBottomButtons/ModalBottomButtons';
import { TransferCurriculaVersion } from '../modals/TransferCurriculaModal';
import styles from './TransferCurriculaForm.module.scss';
import { isCurriculumComplete, TransferCurriculaList, TransferCurriculaRow } from './TransferCurriculaList';

type TransferCurriculaFormProps = {
  version: TransferCurriculaVersion;
  onLoad: (isLoading: boolean) => void;
  onClose: () => void;
};

export const TransferCurriculaForm = ({ version, onLoad, onClose }: TransferCurriculaFormProps) => {
  const { t } = useTranslation();
  const { pimAuthClaims } = useAuthClaims();
  const organizationUuid = pimAuthClaims.getOrganizationUuid();
  const schoolYear = useSelectedSchoolYear();
  const lessonsContext = useMemorizedCacheTags(['LESSONS', 'LESSON_CLASS']);

  const incompleteSelection = useDefaultSelecting();

  const [, createLessonsFromCurricula] = useCreateLessonsFromCurriculaMutation();

  const curriculaContext = useMemorizedCacheTag('CURRICULA');
  const [{ data: curriculaData }] = useCurriculaListQuery({
    variables: { curriculumVersionUuid: version.uuid },
    context: curriculaContext,
  });
  const curricula = curriculaData?.curricula;

  const curriculumUuids = useMemo(() => curricula?.map((c) => c.uuid) ?? [], [curricula]);

  const [{ data: statusData, fetching }] = useCurriculaLessonStatusQuery({
    variables: { organizationUuid, schoolYearUuid: schoolYear?.uuid ?? '', curriculumUuids },
    context: lessonsContext,
    pause: curriculumUuids.length === 0 || !schoolYear,
  });

  const rows: TransferCurriculaRow[] = useMemo(() => {
    const statusByUuid = new Map((statusData?.curriculaLessonStatus ?? []).map((s) => [s.curriculumUuid, s]));

    return (
      (curricula ?? [])
        .map((curriculum) => {
          const status = statusByUuid.get(curriculum.uuid);
          // Kartentypen je (Label, Dauer) einmalig: Fachstunde bzw. Kürzel des Fachbereichs.
          const typeLabels = Object.values(
            curriculum.curriculumUnits.reduce<Record<string, string>>((acc, unit) => {
              const label = unit.subjectContainer?.shortName ?? t('FS');
              acc[`${label}_${unit.duration}`] = label;
              return acc;
            }, {}),
          );

          // Jahrgänge und Jahrgangsgruppen aggregiert – nur für den ExpertFilter.
          const gradeSet = new Set<string>();
          const gradeGroupSet = new Set<string>();
          curriculum.curriculumGrades.forEach((cg) => {
            (cg.grades ?? []).forEach((grade) => gradeSet.add(String(grade)));
            (cg.gradeGroups ?? []).forEach((gg) => gg?.name && gradeGroupSet.add(gg.name));
          });

          return {
            uuid: curriculum.uuid,
            subjectName: curriculum.subject.name,
            classNames: [...curriculum.groupGradeNames]
              .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
              .join(' | '),
            typeLabels,
            isEpochPlan: curriculum.subject.isSubjectContainer,
            personsCount: curriculum.persons.length,
            elective: curriculum.elective ?? false,
            teachingLoadEnabled: curriculum.teachingLoadEnabled ?? false,
            existing: status?.existing ?? 0,
            possible: status?.possible ?? 0,
            grade: [...gradeSet].sort((a, b) => a.localeCompare(b, undefined, { numeric: true })).join(', '),
            gradeGroup: [...gradeGroupSet].sort((a, b) => a.localeCompare(b)).join(', '),
          };
        })
        // Nur Sollstunden, die überhaupt übernommen werden können (possible > 0).
        .filter((row) => row.possible > 0)
        .sort((a, b) => a.classNames.localeCompare(b.classNames, undefined, { numeric: true }))
    );
  }, [curricula, statusData, t]);

  const incomplete = useMemo(() => rows.filter((row) => !isCurriculumComplete(row)), [rows]);
  const complete = useMemo(() => rows.filter((row) => isCurriculumComplete(row)), [rows]);

  const selectedCount = Object.keys(incompleteSelection.rowSelection).length;

  async function handleSubmit() {
    const curriculumUuidsToImport = selectedToData<TransferCurriculaRow>(
      incompleteSelection.rowSelection,
      incomplete,
    ).map((row) => row.uuid);
    if (curriculumUuidsToImport.length === 0) return;

    onLoad(true);
    const response = await createLessonsFromCurricula(
      { organizationUuid, schoolYearUuid: schoolYear?.uuid ?? '', curriculumUuids: curriculumUuidsToImport },
      lessonsContext,
    );
    onLoad(false);

    if (response.error) {
      showUserErrorToast({ error: response.error });
      return;
    }

    const result = response.data?.createLessonsFromCurricula;
    if (result?.failed.length) {
      showUserErrorToast({ text: t('lesson.transfer.failed', { count: result.failed.length }), error: result.failed });
    }
    showSuccessToast(
      t('lesson.transfer.success', {
        created: result?.createdLessonUuids.length ?? 0,
        skipped: result?.skipped.length ?? 0,
      }),
    );

    onClose();
  }

  return (
    <div className={styles['transfer-curricula-form']}>
      <TabBar
        className={styles['filter-bar']}
        backgroundColor={'none'}
        tabs={[
          {
            trigger: `${t('lesson.transfer.tabIncomplete')} (${incomplete.length})`,
            id: 'incomplete',
            content: (
              <TransferCurriculaList
                rows={incomplete}
                loading={fetching}
                rowSelection={incompleteSelection.rowSelection}
                onRowSelectionChange={incompleteSelection.onRowSelectionChange}
              />
            ),
          },
          {
            trigger: `${t('lesson.transfer.tabComplete')} (${complete.length})`,
            id: 'complete',
            content: <TransferCurriculaList rows={complete} loading={fetching} />,
          },
        ]}
      />

      <ModalBottomButtons
        closeButton={{ callback: onClose }}
        submitButton={{ text: t('common.inherit'), callback: handleSubmit, disabled: selectedCount === 0 }}
        className='px-6'
      />
    </div>
  );
};
